/**
 * AddSwapWrapper - Translation layer between new hooks architecture and legacy AddSwapRecipe modal
 *
 * This component:
 * 1. Consumes data from the new context providers
 * 2. Translates it into the format expected by AddSwapRecipe
 * 3. Provides selection handlers that use the new CRUD operations
 * 4. Manages modal state through modalManager
 */

import React, { useCallback, useMemo, Component } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import AddSwapRecipe from '@components/Search/Modals/AddSwapRecipe.react';
import { useMealPlanData } from '@components/Plans/Editor/contexts';
import { useAssets } from '@components/Plans/Editor/contexts';
import { useMealOperations } from '@components/Plans/Editor/contexts';
import { useFoodSelection } from '../hooks/useFoodSelection';
import UserStore from '@stores/UserStore';
import Analytics from '@utils/Analytics';
import { getMealTypeInfoByName, shouldCreateLeftovers, normalizeParticipantsToArray, getParticipantsForSpecificMeal } from '@utils/Meals';
import { getExcessServings, calculateScalingWithLeftovers } from '@utils/servingsCalculations';

/**
 * Generate a short 8-character UUID for meals
 * @returns {string} 8-character UUID
 */
const generateShortUuid = () => {
    return uuid().substring(0, 8);
};

/**
 * Create a plan meal for a recipe, matching the old pattern
 * @returns {Object} Plan meal
 */
const createRecipeMeal = (recipe, offset, mealType, profile, scaling, participants) => {
    const side_dish =
        ['Breakfast Side Dish', 'Lunch Side Dish', 'Side Dish'].filter((t) => (recipe.tags || []).includes(t))
            .length > 0;

    return {
        id: generateShortUuid(),
        meal_type: 'fresh',
        logged_amount: profile.portion,
        logged_unit: 'serving',
        logged_grams: (Math.round(recipe.grams_per_serving * 1000) / 1000) * profile.portion,
        logged_milliliters: (Math.round(recipe.milliliters_per_serving * 1000) / 1000) * profile.portion,
        meal: mealType.name,
        offset: offset,
        recipe_uuid: recipe.uuid,
        details_uuid: recipe.details,
        recipe_title: recipe.title,
        recipe_image: recipe.image,
        scaling,
        participants: participants ? participants.map((p) => p.uuid || '').filter(Boolean) : ['USER'],
        side_dish,
    };
};

// Legacy context provider for AddSwapRecipe
class LegacyContextProvider extends Component {
    static propTypes = {
        addSwapContext: PropTypes.object.isRequired,
        selectionHandlers: PropTypes.object.isRequired,
        children: PropTypes.node.isRequired,
    };

    static childContextTypes = {
        addSwapContext: PropTypes.object,
        profile: PropTypes.object,
        onSelectRecipe: PropTypes.func,
        onSelectCombo: PropTypes.func,
        onSelectFood: PropTypes.func,
        onSelectProducts: PropTypes.func,
        onSelectFrequentlyUsed: PropTypes.func,
    };

    getChildContext() {
        const { addSwapContext, selectionHandlers } = this.props;

        return {
            addSwapContext,
            profile: addSwapContext.profile,
            onSelectRecipe: selectionHandlers.onSelectRecipe,
            onSelectCombo: selectionHandlers.onSelectCombo,
            onSelectFood: selectionHandlers.onSelectFood,
            onSelectProducts: selectionHandlers.onSelectProducts,
            onSelectFrequentlyUsed: selectionHandlers.onSelectFrequentlyUsed,
        };
    }

    render() {
        return this.props.children;
    }
}

const AddSwapWrapper = ({ modalManager }) => {
    // Get data from new context providers
    const { profile, plan } = useMealPlanData();
    const { recipes, foods, details, merchants, brands, locations } = useAssets();
    const mealOperations = useMealOperations();

    // Determine which modal is open and get its context
    const isModalOpen = modalManager.openModals?.addSwapRecipe;
    const modalContext = modalManager.addSwapContext;

    // Use the food selection hook
    const { createOnSelectFoodHandler } = useFoodSelection({
        modalManager,
        mealOperations,
        profile
    });

    if (!isModalOpen || !modalContext) {
        return null;
    }

    // Helper to get Analytics context for meal plan operations
    const getAnalyticsContext = useCallback((mealData = {}) => {
        const { offset, mealType } = modalContext;
        return {
            'Meal Plan UUID': plan.uuid,
            'Meal Plan Name': plan.title,
            'Offset': mealData.offset || offset,
            'Meal Type': mealData.meal || mealType.name
        };
    }, [plan, modalContext]);

    // Helper function to handle meal replacement/addition - DRY principle
    const handleMealAction = useCallback((newMeals) => {
        const { mealsToDelete } = modalContext;
        const mealsArray = Array.isArray(newMeals) ? newMeals : [newMeals];

        if (mealsToDelete && mealsToDelete.length > 0) {
            // Replace mode: hard delete existing meals and add new ones
            mealOperations.batchOperations((batchOps) => {
                mealsToDelete.forEach((meal) => {
                    batchOps.removeMeal(meal.id || meal.uuid, true); // hardDelete = true for swaps
                });
                batchOps.addMeals(mealsArray);
            });
        } else {
            // Add mode: just add the new meal(s)
            mealOperations.addMeals(mealsArray);
        }

        // Close the modal
        modalManager.closeModal('addSwapRecipe');
    }, [modalContext, mealOperations, modalManager]);

    // The leftover slots of the fresh meals already in this meal slot
    const getExistingLeftoverSlots = (offset, mealTypeName) => {
        const existingFreshMealsInSlot = mealOperations.getMealsByOffsetAndType(offset, mealTypeName)
            .filter(meal => meal.meal_type !== 'leftover' && !meal.deleted);

        const leftoverSlotMap = new Map();
        existingFreshMealsInSlot.forEach(existingMeal => {
            mealOperations.getLeftoversForMeal(existingMeal.id).forEach(leftoverMeal => {
                if (!leftoverMeal.deleted) {
                    const key = `${leftoverMeal.offset}_${leftoverMeal.meal}`;
                    if (!leftoverSlotMap.has(key)) {
                        leftoverSlotMap.set(key, {
                            leftoverOffset: leftoverMeal.offset,
                            mealType: leftoverMeal.meal,
                            participants: leftoverMeal.participants,
                        });
                    }
                }
            });
        });

        return Array.from(leftoverSlotMap.values());
    };

    // Participant objects of every leftover slot, one entry per participant per slot
    const getLeftoverSlotParticipants = (leftoverSlots, mainParticipantUuids) => {
        return leftoverSlots.flatMap(({ participants }) => {
            return getParticipantsForSpecificMeal({ participants: participants || mainParticipantUuids }, profile);
        });
    };

    // Selection handler for recipes
    const onSelectRecipe = useCallback(
        (recipe, participants = null, scaling = null, searchMetadata = {}, leftoverOffsets = null) => {

            const { offset, mealType, mealsToDelete } = modalContext;
            const scalingProvided = !!scaling;

            // Calculate scaling if not provided
            const needed = participants
                ? participants.reduce((total, member) => total + member.portion, 0)
                : profile.portion;
            if (!scaling) {
                scaling = Math.ceil(needed / recipe.servings);
            }

            const newMeal = createRecipeMeal(recipe, offset, mealType, profile, scaling, participants);

            // Analytics tracking for recipe selection
            Analytics.addRecipe({
                UUIDs: [recipe.uuid],
                'Recipe Name': recipe.title,
                ...getAnalyticsContext(newMeal)
            });

            // LEFTOVER HANDLING LOGIC
            // If leftoverOffsets are provided (from modal callback), create leftovers based on user selection
            if (leftoverOffsets !== null) {
                // Defensive check: ensure leftoverOffsets is an array
                if (!Array.isArray(leftoverOffsets)) {
                    return;
                }

                const mealsToAdd = [newMeal]; // Start with the main meal

                // Create leftover meals based on user-selected offsets
                leftoverOffsets.forEach(({ leftoverOffset, mealType: leftoverMealType, participants: leftoverParticipants }) => {
                    const leftoverMeal = {
                        ...newMeal,
                        id: generateShortUuid(),
                        meal_type: 'leftover',
                        offset: leftoverOffset,
                        meal: leftoverMealType,
                        parent: newMeal.id,
                        participants: leftoverParticipants ? leftoverParticipants.map(p => p.uuid || '').filter(Boolean) : normalizeParticipantsToArray(newMeal.participants),
                    };
                    mealsToAdd.push(leftoverMeal);
                });

                // Handle meal replacement/addition with all meals (main + leftovers)
                handleMealAction(mealsToAdd);
                return;
            }

            // If this meal slot already has existing leftover meals, auto-add the new recipe to those
            // same slots instead of showing the leftover modal.
            if (leftoverOffsets === null || leftoverOffsets === undefined) {
                const existingLeftoverSlots = getExistingLeftoverSlots(offset, mealType.name);

                if (existingLeftoverSlots.length > 0) {
                    if (!scalingProvided) {
                        newMeal.scaling = calculateScalingWithLeftovers(
                            participants || getParticipantsForSpecificMeal(newMeal, profile),
                            getLeftoverSlotParticipants(existingLeftoverSlots, newMeal.participants),
                            newMeal,
                            recipe
                        );
                    }

                    const mealsToAdd = [newMeal];
                    existingLeftoverSlots.forEach(({ leftoverOffset, mealType: leftoverMealType, participants: leftoverParticipants }) => {
                        mealsToAdd.push({
                            ...newMeal,
                            id: generateShortUuid(),
                            meal_type: 'leftover',
                            offset: leftoverOffset,
                            meal: leftoverMealType,
                            parent: newMeal.id,
                            participants: normalizeParticipantsToArray(leftoverParticipants || newMeal.participants),
                        });
                    });
                    handleMealAction(mealsToAdd);
                    return;
                }
            }

            // Check if leftovers should be created (and prompt user with modal)
            if (shouldCreateLeftovers(recipe, scaling, participants, profile)) {
                // Check if there are already leftover dishes in this meal slot
                const existingMealsInSlot = mealOperations.getMealsByOffsetAndType(offset, mealType.name);
                const hasLeftoverDishesInMeal = existingMealsInSlot.some(meal =>
                    meal.meal_type === 'leftover' &&
                    !meal.deleted
                );

                // Only prompt for leftovers if there aren't already leftover dishes in this meal slot
                if (!hasLeftoverDishesInMeal) {
                    // Open LeftoverOffsetModal to let user choose leftover dates
                    // When user confirms, the modal will call this same callback with leftoverOffsets
                    const leftoverParticipants = participants || [{ uuid: 'USER', name: profile.name, portion: profile.portion }];
                    modalManager.openLeftoverOffsetsModal({
                        meal: newMeal,
                        leftoverOffset: offset,
                        participants: leftoverParticipants,
                        recipe: recipe,
                        scaling: scaling,
                        mealsToDelete: mealsToDelete || [], // Pass mealsToDelete for swap mode
                        isRecipeSelection: true, // Flag to identify this came from recipe selection (hide swap in conflicts)
                        yieldExcess: getExcessServings(recipe, scaling, leftoverParticipants),
                        onSelectLeftoverOffsets: (selectedOffsets, scalingMap, extraServings) => {
                            // User confirmed leftover selection - close modal and call onSelectRecipe again with the offsets
                            modalManager.closeLeftoverOffsetsModal();
                            onSelectRecipe(recipe, participants, scaling, searchMetadata, selectedOffsets);
                        }
                    });
                    return; // Exit and wait for user to select leftover dates
                }
            }

            // No leftovers to create - just add the main meal
            handleMealAction([newMeal]);
        },
        [modalContext, mealOperations, profile, modalManager, handleMealAction, getAnalyticsContext]
    );

    // Selection handler for combo meals
    const onSelectCombo = useCallback(
        (combo, mainRecipe, sideRecipe, participants, scaling = null, leftoverOffsets = null) => {
            const { offset, mealType, mealsToDelete } = modalContext;

            // Calculate total servings needed based on participants
            const needed = participants
                ? participants.reduce((total, member) => total + member.portion, 0)
                : profile.portion;

            // Calculate scaling independently for each dish so side dish lasts the same number of days as main
            const mainScaling = scaling !== null
                ? scaling
                : Math.ceil(needed / mainRecipe.servings);

            // Calculate how many days the main dish will last, then scale side to match
            const totalMainDays = Math.floor(mainScaling * mainRecipe.servings / needed);
            const totalSideServingsNeeded = totalMainDays * needed;
            const sideScaling = Math.ceil(totalSideServingsNeeded / sideRecipe.servings);

            // Create main meal matching the old pattern
            const mainMeal = {
                id: generateShortUuid(),
                meal_type: 'fresh',
                logged_amount: profile.portion,
                logged_unit: 'serving',
                logged_grams: (Math.round(mainRecipe.grams_per_serving * 1000) / 1000) * profile.portion,
                logged_milliliters: (Math.round(mainRecipe.milliliters_per_serving * 1000) / 1000) * profile.portion,
                meal: mealType.name,
                offset: offset,
                recipe_uuid: mainRecipe.uuid,
                details_uuid: mainRecipe.details,
                recipe_title: mainRecipe.title,
                recipe_image: mainRecipe.image,
                scaling: mainScaling,
                participants: participants ? participants.map((p) => p.uuid || '').filter(Boolean) : ['USER'],
            };

            // Create side meal matching the old pattern
            const sideMeal = {
                id: generateShortUuid(),
                meal_type: 'fresh',
                logged_amount: profile.portion,
                logged_unit: 'serving',
                logged_grams: (Math.round(sideRecipe.grams_per_serving * 1000) / 1000) * profile.portion,
                logged_milliliters: (Math.round(sideRecipe.milliliters_per_serving * 1000) / 1000) * profile.portion,
                meal: mealType.name,
                offset: offset,
                recipe_uuid: sideRecipe.uuid,
                side_dish: true,
                details_uuid: sideRecipe.details,
                recipe_title: sideRecipe.title,
                recipe_image: sideRecipe.image,
                scaling: sideScaling,
                participants: participants ? participants.map((p) => p.uuid || '').filter(Boolean) : ['USER'],
            };

            // Analytics tracking for combo selection (track both recipes)
            Analytics.addRecipe({
                UUIDs: [mainRecipe.uuid, sideRecipe.uuid],
                'Recipe Name': `${mainRecipe.title} + ${sideRecipe.title}`,
                ...getAnalyticsContext(mainMeal)
            });

            // If leftoverOffsets are provided (from modal callback), create leftovers based on user selection
            if (leftoverOffsets !== null) {
                if (Array.isArray(leftoverOffsets)) {
                    const mealsToAdd = [mainMeal, sideMeal];
                    leftoverOffsets.forEach(({ leftoverOffset, mealType: leftoverMealType, participants: leftoverParticipants }) => {
                        const normalizedParticipants = leftoverParticipants ? leftoverParticipants.map(p => p.uuid || '').filter(Boolean) : [...mainMeal.participants];
                        mealsToAdd.push(
                            { ...mainMeal, id: generateShortUuid(), meal_type: 'leftover', offset: leftoverOffset, meal: leftoverMealType, parent: mainMeal.id, participants: normalizedParticipants },
                            { ...sideMeal, id: generateShortUuid(), meal_type: 'leftover', offset: leftoverOffset, meal: leftoverMealType, parent: sideMeal.id, participants: normalizedParticipants }
                        );
                    });
                    handleMealAction(mealsToAdd);
                }
                return;
            }

            // If this meal slot already has existing leftover meals, auto-add both dishes to those
            // same slots instead of showing the leftover modal.
            const existingLeftoverSlots = getExistingLeftoverSlots(offset, mealType.name);

            if (existingLeftoverSlots.length > 0) {
                if (scaling === null) {
                    const mainParticipants = participants || getParticipantsForSpecificMeal(mainMeal, profile);
                    const leftoverParticipants = getLeftoverSlotParticipants(existingLeftoverSlots, mainMeal.participants);

                    mainMeal.scaling = calculateScalingWithLeftovers(mainParticipants, leftoverParticipants, mainMeal, mainRecipe);
                    sideMeal.scaling = calculateScalingWithLeftovers(mainParticipants, leftoverParticipants, sideMeal, sideRecipe);
                }

                const mealsToAdd = [mainMeal, sideMeal];
                existingLeftoverSlots.forEach(({ leftoverOffset, mealType: leftoverMealType, participants: leftoverParticipants }) => {
                    mealsToAdd.push(
                        { ...mainMeal, id: generateShortUuid(), meal_type: 'leftover', offset: leftoverOffset, meal: leftoverMealType, parent: mainMeal.id, participants: normalizeParticipantsToArray(leftoverParticipants || mainMeal.participants) },
                        { ...sideMeal, id: generateShortUuid(), meal_type: 'leftover', offset: leftoverOffset, meal: leftoverMealType, parent: sideMeal.id, participants: normalizeParticipantsToArray(leftoverParticipants || sideMeal.participants) }
                    );
                });
                handleMealAction(mealsToAdd);
                return;
            }

            // Check if leftovers should be created (and prompt user with modal)
            if (shouldCreateLeftovers(mainRecipe, mainScaling, participants, profile)) {
                // Check if there are already leftover dishes in this meal slot
                const existingMealsInSlot = mealOperations.getMealsByOffsetAndType(offset, mealType.name);
                const hasLeftoverDishesInMeal = existingMealsInSlot.some(meal =>
                    meal.meal_type === 'leftover' &&
                    !meal.deleted
                );

                // Only prompt for leftovers if there aren't already leftover dishes in this meal slot
                if (!hasLeftoverDishesInMeal) {
                    const leftoverParticipants = participants || [{ uuid: 'USER', name: profile.name, portion: profile.portion }];
                    modalManager.openLeftoverOffsetsModal({
                        meal: mainMeal,
                        leftoverOffset: offset,
                        participants: leftoverParticipants,
                        recipe: mainRecipe,
                        scaling: mainScaling,
                        mealsToDelete: mealsToDelete || [],
                        isRecipeSelection: true,
                        yieldExcess: getExcessServings(mainRecipe, mainScaling, leftoverParticipants),
                        onSelectLeftoverOffsets: (selectedOffsets, scalingMap, extraServings) => {
                            modalManager.closeLeftoverOffsetsModal();
                            onSelectCombo(combo, mainRecipe, sideRecipe, participants, scaling, selectedOffsets);
                        }
                    });
                    return;
                }
            }

            // No leftovers to create - just add the main and side meals
            handleMealAction([mainMeal, sideMeal]);
        },
        [modalContext, mealOperations, profile, modalManager, handleMealAction, getAnalyticsContext]
    );

    // Selection handler for foods - now using the custom hook
    const onSelectFood = createOnSelectFoodHandler(modalContext);

    // Selection handler for products - matches old pattern
    const onSelectProducts = useCallback(
        (foods) => {
            const { offset, mealType, mealsToDelete } = modalContext;

            // Create meals for each food/product
            const newMeals = foods.map((food) => ({
                id: generateShortUuid(),
                meal_type: 'food',
                food_uuid: food.uuid,
                recipe_title: food.pretty_name || food.name,
                meal: mealType.name,
                offset: offset,
                logged_amount: 1,
                logged_unit: 'serving',
                logged_grams: food.grams_per_serving,
                logged_milliliters: food.milliliters_per_serving,
            }));

            // Handle meal replacement/addition
            handleMealAction(newMeals);
        },
        [modalContext, mealOperations, modalManager, handleMealAction]
    );

    // Selection handler for Frequently Used meals: adds every item of the chosen meal
    const onSelectFrequentlyUsed = useCallback(
        (items, assets) => {
            const { offset, mealType } = modalContext;

            const newMeals = items.map((item) => {
                if (item.recipe_uuid) {
                    const recipe = assets.recipes[item.recipe_uuid];

                    return createRecipeMeal(recipe, offset, mealType, profile, Math.ceil(profile.portion / recipe.servings));
                }

                const food = assets.foods[item.food_uuid];

                return {
                    id: generateShortUuid(),
                    meal_type: 'food',
                    food_uuid: food.uuid,
                    recipe_title: food.pretty_name || food.name,
                    meal: mealType.name,
                    offset: offset,
                    logged_amount: item.logged_amount,
                    logged_unit: item.logged_unit,
                    logged_grams: item.logged_grams,
                    logged_milliliters: item.logged_milliliters,
                };
            });

            handleMealAction(newMeals);
        },
        [modalContext, profile, handleMealAction]
    );

    // Prepare selection handlers for context provider
    const selectionHandlers = {
        onSelectRecipe,
        onSelectCombo,
        onSelectFood,
        onSelectProducts,
        onSelectFrequentlyUsed,
    };

    const leftoverParticipants = getLeftoverSlotParticipants(
        getExistingLeftoverSlots(modalContext.offset, modalContext.mealType.name),
        (modalContext.participants || []).map(p => p.uuid)
    );

    // Generate appropriate modal title based on mode and meal type
    const isSwapMode = modalContext.mealsToDelete?.length > 0;
    const mealTypeName = modalContext.mealType?.name?.toUpperCase() || 'MEAL';
    const modalTitle = isSwapMode ? `SWAP ${mealTypeName}` : `ADD ${mealTypeName}`;

    // Render the legacy modal with context provider
    return (
        <LegacyContextProvider addSwapContext={{ ...modalContext, leftoverParticipants }} selectionHandlers={selectionHandlers}>
            <AddSwapRecipe
                closeModal={() => {
                    modalManager.closeModal('addSwapRecipe');
                }}
                profile={profile}
                onSelectRecipe={onSelectRecipe}
                onSelectCombo={onSelectCombo}
                onSelectFood={onSelectFood}
                onSelectProducts={onSelectProducts}
                // Modal settings from enhanced context
                // Fix for Bug #9: Pass defaultMode and allowedModes from swap menu options
                defaultMode={modalContext.options?.defaultMode}
                allowedModes={modalContext.options?.allowedModes}
                // Additional props from modal context
                mode={modalContext.mealsToDelete?.length > 0 ? 'swap' : 'add'}
                targetMeal={modalContext.originalMeal}
                mealsToReplace={modalContext.mealsToDelete}
                // Fix for Defect #3: Dynamic modal title showing correct meal type
                modalTitle={modalTitle}
                {...modalContext?.modalSettings}
            />
        </LegacyContextProvider>
    );
};

export default AddSwapWrapper;
